import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useUIStore, useMovieStore, useAuthStore } from '../../../stores';
import { TrashIcon } from '@radix-ui/react-icons';
import { Button } from '@radix-ui/themes';
import { useState, useRef, useCallback, useEffect } from 'react';
import { useSignalR } from '../../../hooks';
import { useTranslation } from '../../../i18n';
import { fetchVectorPackageFiles, fetchVectorPackages, importVectorPackageFile, resolveAssetUrl, uploadFile, } from '../../../util/api';
import { extractAssetPathFromBody } from '../../../util/imageBody';
import { Toolpad } from '../Toolpad';
import { ConfirmModal } from '../../ui/ConfirmModal';
export function Library() {
    const { t, locale } = useTranslation();
    const { toolpads, toggleToolpad } = useUIStore();
    const { library, addLibraryItem, deleteLibraryItem, currentScene, currentMovie, currentMovieResponse, currentLayer, addSceneObject } = useMovieStore();
    const { status: signalRStatus, connect, sceneObjectCreateFromLibraryItem, sceneObjectsGetByLayer, libraryObjectDelete } = useSignalR();
    const { user, accessToken } = useAuthStore();
    const isOpen = toolpads.library.isOpen;
    const [isDragOver, setIsDragOver] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [deleteCandidate, setDeleteCandidate] = useState(null);
    const [deleteError, setDeleteError] = useState(null);
    const [usageWarning, setUsageWarning] = useState(null);
    const [layerSelectionWarningOpen, setLayerSelectionWarningOpen] = useState(false);
    const fileInputRef = useRef(null);
    const uploadingRef = useRef(false);
    const [librarySource, setLibrarySource] = useState('mine');
    const [vectorPackages, setVectorPackages] = useState([]);
    const [vectorFiles, setVectorFiles] = useState([]);
    const [vectorsLoading, setVectorsLoading] = useState(false);
    const [vectorError, setVectorError] = useState(null);
    useEffect(() => {
        if (!isOpen)
            return;
        let cancelled = false;
        fetchVectorPackages(accessToken)
            .then((list) => {
            if (!cancelled)
                setVectorPackages(list);
        })
            .catch(() => {
            if (!cancelled)
                setVectorError(t('toolpads.library.vectorsLoadFailed'));
        });
        return () => {
            cancelled = true;
        };
    }, [isOpen, accessToken, locale]);
    useEffect(() => {
        if (librarySource === 'mine')
            return;
        if (vectorPackages.length > 0 && !vectorPackages.some((pkg) => pkg.id === librarySource)) {
            setLibrarySource('mine');
        }
    }, [librarySource, vectorPackages]);
    useEffect(() => {
        if (!isOpen || librarySource === 'mine')
            return;
        let cancelled = false;
        setVectorsLoading(true);
        setVectorError(null);
        fetchVectorPackageFiles(librarySource, accessToken)
            .then((files) => {
            if (!cancelled)
                setVectorFiles(files);
        })
            .catch(() => {
            if (!cancelled) {
                setVectorFiles([]);
                setVectorError(t('toolpads.library.vectorsLoadFailed'));
            }
        })
            .finally(() => {
            if (!cancelled)
                setVectorsLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [isOpen, librarySource, accessToken, locale]);
    const resolveTargetLayerId = useCallback(() => {
        const fromSelection = currentLayer ? Number(currentLayer.id) : NaN;
        if (!Number.isNaN(fromSelection) && fromSelection > 0) {
            return fromSelection;
        }
        return null;
    }, [currentLayer]);
    const normalizeObjectSize = useCallback((value) => {
        return typeof value === 'number' && value > 0 ? Math.round(value) : 100;
    }, []);
    const mapSceneObjectResponseToStore = useCallback((response) => {
        let props = {};
        if (response.defaultState?.properties) {
            try {
                props = JSON.parse(response.defaultState.properties);
            }
            catch {
                // ignore invalid json
            }
        }
        const rawSourcePath = response.url ?? response.fullPath ?? extractAssetPathFromBody(response.body);
        const resolvedSrc = rawSourcePath ? resolveAssetUrl(rawSourcePath) : undefined;
        const x = Math.round(props.left ?? props.x ?? response.dropPosition?.left ?? 0);
        const y = Math.round(props.top ?? props.y ?? response.dropPosition?.top ?? 0);
        const width = normalizeObjectSize(props.width ?? response.width);
        const height = normalizeObjectSize(props.height ?? response.height);
        const rotation = props.rotation ?? 0;
        const opacity = props.opacity ?? 1;
        return {
            id: String(response.id),
            type: response.type || 'image',
            name: response.name,
            src: resolvedSrc || undefined,
            libraryObjectId: response.libraryObjectId ?? undefined,
            parentObjectId: response.parentObjectId != null ? String(response.parentObjectId) : null,
            isGroup: response.isGroup === true || response.type === 'group',
            x,
            y,
            width,
            height,
            rotation,
            opacity,
            zIndex: response.sorter ?? currentScene?.objects.length ?? 0,
            visible: response.isVisible,
            locked: response.isLocked,
            pivotPoint: response.pivotPoint?.trim() ? response.pivotPoint : 'center',
            layerId: response.layerId,
            defaultPose: { x, y, width, height, rotation, opacity },
            states: [],
        };
    }, [currentScene?.objects.length, normalizeObjectSize]);
    const hasLibraryItemDrag = (e) => {
        const types = Array.from(e.dataTransfer.types ?? []);
        return types.includes('application/x-prototype-library-item') || types.includes('application/json');
    };
    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        // Only show drag-over for file uploads, not when dragging a library item out
        if (hasLibraryItemDrag(e))
            return;
        setIsDragOver(true);
    }, []);
    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    }, []);
    const uploadImages = useCallback(async (files) => {
        if (!currentMovie) {
            console.error('No movie selected');
            return;
        }
        if (!user?.id) {
            console.error('User not authenticated');
            return;
        }
        const images = files.filter((file) => file.type.startsWith('image/'));
        if (images.length === 0 || uploadingRef.current)
            return;
        uploadingRef.current = true;
        setIsUploading(true);
        setUploadProgress(0);
        try {
            for (let index = 0; index < images.length; index += 1) {
                const file = images[index];
                try {
                    const uploaded = await uploadFile(file, parseInt(currentMovie.id, 10), (progress) => {
                        const overall = ((index + progress / 100) / images.length) * 100;
                        setUploadProgress(Math.round(overall));
                    }, accessToken);
                    const newItem = {
                        id: String(uploaded.id),
                        name: uploaded.name || file.name.replace(/\.[^/.]+$/, ''),
                        type: 'image',
                        src: uploaded.url ? resolveAssetUrl(uploaded.url) : '',
                        thumbnail: uploaded.thumbnailUrl ? resolveAssetUrl(uploaded.thumbnailUrl) : undefined,
                    };
                    addLibraryItem(newItem);
                }
                catch (error) {
                    console.error('File upload failed:', error);
                }
            }
        }
        finally {
            uploadingRef.current = false;
            setIsUploading(false);
            setUploadProgress(0);
        }
    }, [addLibraryItem, accessToken, currentMovie, user]);
    const handleDrop = useCallback(async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        await uploadImages(Array.from(e.dataTransfer.files));
    }, [uploadImages]);
    const handleImportClick = () => {
        if (isUploading)
            return;
        fileInputRef.current?.click();
    };
    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files ?? []);
        e.target.value = '';
        await uploadImages(files);
    };
    const libraryItemFromUpload = (uploaded, fallbackName) => ({
        id: String(uploaded.id),
        name: uploaded.name || fallbackName,
        type: 'image',
        src: uploaded.url ? resolveAssetUrl(uploaded.url) : '',
        thumbnail: uploaded.thumbnailUrl ? resolveAssetUrl(uploaded.thumbnailUrl) : undefined,
    });
    const handleVectorDragStart = (e, file) => {
        const payload = JSON.stringify({
            type: 'vectorPackageFile',
            packageId: file.packageId,
            fileId: file.id,
            name: file.name,
            src: resolveAssetUrl(file.url),
            width: file.width,
            height: file.height,
        });
        e.dataTransfer.setData('application/x-prototype-vector-file', payload);
        e.dataTransfer.setData('application/json', payload);
        e.dataTransfer.effectAllowed = 'copy';
    };
    const handleUseVector = async (file) => {
        if (!currentMovie)
            return;
        setVectorError(null);
        try {
            const uploaded = await importVectorPackageFile(file.packageId, file.id, Number(currentMovie.id), accessToken);
            const item = libraryItemFromUpload(uploaded, file.name);
            addLibraryItem(item);
            await handleAddToStage(item);
        }
        catch (error) {
            console.error('[Library] Failed to import vector package file:', error);
            setVectorError(t('toolpads.library.vectorUseFailed'));
        }
    };
    const handleLibraryItemDragStart = (e, item) => {
        const payload = JSON.stringify({ type: 'libraryItem', item });
        e.dataTransfer.setData('application/x-prototype-library-item', payload);
        e.dataTransfer.setData('application/json', payload);
        e.dataTransfer.effectAllowed = 'copy';
    };
    const handleAddToStage = async (item) => {
        if (!currentScene || !currentMovie)
            return;
        const targetLayerId = resolveTargetLayerId();
        if (!targetLayerId) {
            setLayerSelectionWarningOpen(true);
            return;
        }
        const libraryObjectId = Number(item.id);
        if (!Number.isFinite(libraryObjectId)) {
            console.warn('[Library] Invalid library object id for persisted create', item.id);
            return;
        }
        const centerX = Math.floor(currentMovie.width / 2) - 50;
        const centerY = Math.floor(currentMovie.height / 2) - 50;
        const payload = {
            id: libraryObjectId,
            movieId: Number(currentMovie.id),
            name: item.name,
            description: '',
            type: item.type,
            fileName: '',
            width: 100,
            height: 100,
            dropPosition: { top: centerY, left: centerX },
        };
        try {
            if (signalRStatus !== 'Connected')
                await connect();
            const created = await sceneObjectCreateFromLibraryItem(payload, Number(currentScene.id), targetLayerId);
            const mapped = mapSceneObjectResponseToStore(created);
            if (!mapped.src && item.src)
                mapped.src = item.src;
            addSceneObject(mapped);
        }
        catch (error) {
            console.error('[Library] Failed to persist library item on stage:', error);
        }
    };
    const getLibraryItemUsageCount = useCallback(async (libraryObjectId) => {
        if (!currentMovieResponse) {
            return (currentScene?.objects ?? []).filter((sceneObject) => sceneObject.libraryObjectId === libraryObjectId).length;
        }
        if (signalRStatus !== 'Connected') {
            await connect();
        }
        let usageCount = 0;
        for (const sceneResponse of currentMovieResponse.scenes) {
            const layeredObjects = await sceneObjectsGetByLayer(sceneResponse);
            for (const layer of layeredObjects) {
                for (const sceneObject of layer.sceneObjects ?? []) {
                    if (sceneObject.libraryObjectId === libraryObjectId) {
                        usageCount += 1;
                    }
                }
            }
        }
        return usageCount;
    }, [connect, currentMovieResponse, currentScene?.objects, sceneObjectsGetByLayer, signalRStatus]);
    const handleDeleteLibraryItem = async (item) => {
        const libraryObjectId = Number(item.id);
        if (!Number.isFinite(libraryObjectId)) {
            console.warn('[Library] Invalid library object id for delete', item.id);
            return;
        }
        try {
            const usageCount = await getLibraryItemUsageCount(libraryObjectId);
            if (usageCount > 0) {
                setUsageWarning({ name: item.name, usageCount });
                return;
            }
            setDeleteCandidate(item);
        }
        catch (error) {
            console.error('[Library] Failed to check library item usage before delete:', error);
        }
    };
    const confirmDeleteLibraryItem = async (item) => {
        if (!currentMovie)
            return;
        const libraryObjectId = Number(item.id);
        if (!Number.isFinite(libraryObjectId)) {
            console.warn('[Library] Invalid library object id for delete', item.id);
            setDeleteCandidate(null);
            return;
        }
        setDeleteError(null);
        try {
            if (signalRStatus !== 'Connected') {
                await connect();
            }
            await libraryObjectDelete(libraryObjectId);
            deleteLibraryItem(item.id);
        }
        catch (error) {
            console.error('[Library] Failed to delete library item:', error);
            const raw = error instanceof Error ? error.message : String(error);
            setDeleteError(raw || t('toolpads.library.deleteFailedFallback'));
        }
        finally {
            setDeleteCandidate(null);
        }
    };
    return (_jsxs(Toolpad, { title: t('toolpads.library.title'), isOpen: isOpen, onToggle: () => toggleToolpad('library'), children: [_jsx(ConfirmModal, { open: deleteCandidate !== null, onOpenChange: (open) => {
                    // Radix kapanisini onClick'ten once isleyebilir; adayi hemen silme (confirm item'i closure'da yakalar).
                    if (!open)
                        setTimeout(() => setDeleteCandidate(null), 0);
                }, title: t('toolpads.library.deleteConfirmTitle'), description: deleteCandidate
                    ? t('toolpads.library.deleteConfirmDescription', { name: deleteCandidate.name })
                    : undefined, confirmText: t('common.delete'), cancelText: t('common.cancel'), onConfirm: () => {
                    const item = deleteCandidate;
                    if (item)
                        void confirmDeleteLibraryItem(item);
                } }), _jsx(ConfirmModal, { open: deleteError !== null, onOpenChange: (open) => {
                    if (!open)
                        setDeleteError(null);
                }, title: t('toolpads.library.deleteFailedTitle'), description: deleteError ?? undefined, confirmText: t('common.ok'), cancelText: t('common.close'), onConfirm: () => setDeleteError(null) }), _jsx(ConfirmModal, { open: usageWarning !== null, onOpenChange: (open) => {
                    if (!open)
                        setUsageWarning(null);
                }, title: t('toolpads.library.inUseTitle'), description: usageWarning
                    ? t('toolpads.library.inUseDescription', {
                        name: usageWarning.name,
                        count: usageWarning.usageCount,
                    })
                    : undefined, confirmText: t('common.ok'), cancelText: t('common.close'), onConfirm: () => setUsageWarning(null) }), _jsx(ConfirmModal, { open: layerSelectionWarningOpen, onOpenChange: setLayerSelectionWarningOpen, title: t('toolpads.library.noLayerTitle'), description: t('toolpads.library.noLayerDescription'), confirmText: t('common.ok'), cancelText: t('common.close'), onConfirm: () => setLayerSelectionWarningOpen(false) }), _jsxs("div", { className: "library-source-toolbar", role: "toolbar", "aria-label": t('toolpads.library.title'), children: [_jsx("button", { type: "button", className: librarySource === 'mine' ? 'active' : '', onClick: () => setLibrarySource('mine'), children: t('toolpads.library.mine') }), vectorPackages.map((pkg) => (_jsx("button", { type: "button", className: librarySource === pkg.id ? 'active' : '', title: pkg.name, onClick: () => setLibrarySource(pkg.id), children: pkg.name }, pkg.id)))] }), vectorError ? _jsx("p", { className: "library-vector-error", children: vectorError }) : null, librarySource === 'mine' ? (_jsxs("div", { className: `library-content ${isDragOver ? 'drag-over' : ''} ${isUploading ? 'uploading' : ''}`, "aria-busy": isUploading, onDragOver: handleDragOver, onDragLeave: handleDragLeave, onDrop: handleDrop, children: [_jsx("input", { ref: fileInputRef, type: "file", accept: "image/*", multiple: true, onChange: handleFileChange, style: { display: 'none' }, "aria-label": t('toolpads.library.importAria') }), library.length === 0 ? (_jsx("div", { className: "library-empty", children: _jsxs("div", { className: "library-drop-zone", children: [_jsx("p", { children: "\uD83D\uDCF7" }), _jsx("p", { children: t('toolpads.library.dropImages') }), _jsx("p", { children: t('common.or') }), _jsx("button", { className: "btn", onClick: handleImportClick, disabled: isUploading, children: t('toolpads.library.importFiles') })] }) })) : (_jsxs(_Fragment, { children: [_jsx("div", { className: "library-grid", children: library.map((item) => (_jsxs("div", { className: "library-item relative", title: item.name, draggable: true, onDragStart: (e) => handleLibraryItemDragStart(e, item), onDoubleClick: () => handleAddToStage(item), children: [item.thumbnail || item.src ? (_jsx("img", { src: item.thumbnail || item.src, alt: item.name, onError: (e) => {
                                                const target = e.currentTarget;
                                                // Thumbnail yoksa veya zaten ana görsele düştüysek tekrar deneme (aksi halde
                                                // tarayıcı mutlak URL'yi encode eder; ham item.src ile asla eşleşmez → sonsuz onError).
                                                if (item.thumbnail && item.src && !target.dataset.switchedToMain) {
                                                    target.dataset.switchedToMain = '1';
                                                    target.src = item.src;
                                                    return;
                                                }
                                                target.onerror = null;
                                            } })) : (_jsx("div", { className: "library-item-placeholder", children: item.type === 'clip' ? '🎬' : '📷' })), _jsx("span", { className: "library-item-name", children: item.name }), _jsx("div", { className: "library-item-actions absolute top-0 right-0 flex items-center gap-2", children: _jsx(Button, { variant: "surface", radius: "none", size: "1", onClick: () => handleDeleteLibraryItem(item), children: _jsx(TrashIcon, { className: "w-4 h-4" }) }) })] }, item.id))) }), _jsx("div", { className: "library-actions", children: _jsx("button", { className: "btn", onClick: handleImportClick, disabled: isUploading, children: isUploading ? t('toolpads.library.uploading') : t('toolpads.library.import') }) })] })), isUploading ? (_jsxs("div", { className: "library-upload-overlay", role: "status", "aria-live": "polite", children: [_jsx("div", { className: "library-upload-spinner", "aria-hidden": "true" }), _jsxs("span", { className: "library-upload-label", children: [t('toolpads.library.uploading'), uploadProgress > 0 ? ` ${uploadProgress}%` : ''] })] })) : null] })) : (_jsxs("div", { className: "library-content", children: [_jsx("p", { className: "library-vector-hint", children: t('toolpads.library.vectorHint') }), vectorsLoading ? (_jsx("p", { className: "library-vector-hint", children: t('common.loading') })) : vectorFiles.length === 0 ? (_jsx("div", { className: "library-empty", children: _jsx("p", { children: t('toolpads.library.vectorsEmpty') }) })) : (_jsx("div", { className: "library-grid", children: vectorFiles.map((file) => (_jsxs("div", { className: "library-item library-item-vector", title: file.name, draggable: true, onDragStart: (event) => handleVectorDragStart(event, file), onDoubleClick: () => void handleUseVector(file), children: [_jsx("img", { src: resolveAssetUrl(file.url), alt: file.name }), _jsx("span", { className: "library-item-name", children: file.name })] }, file.id))) }))] }))] }));
}
